/**
 * End-to-End Encryption Module using Web Crypto API
 * Uses RSA-OAEP for key exchange and AES-GCM for message encryption
 */

const ALGORITHM = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

const AES_ALGORITHM = {
  name: 'AES-GCM',
  length: 256,
};

// Convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Convert Base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Generate a new RSA key pair for a user
export const generateKeyPair = async () => {
  const keyPair = await window.crypto.subtle.generateKey(
    ALGORITHM,
    true, // extractable
    ['encrypt', 'decrypt']
  );

  // Export public key to share with others
  const publicKeyExported = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
  const publicKeyBase64 = arrayBufferToBase64(publicKeyExported);

  // Export private key to store locally
  const privateKeyExported = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
  const privateKeyBase64 = arrayBufferToBase64(privateKeyExported);

  return {
    publicKey: publicKeyBase64,
    privateKey: privateKeyBase64,
  };
};

// Import a public key from Base64
const importPublicKey = async (publicKeyBase64) => {
  const keyData = base64ToArrayBuffer(publicKeyBase64);
  return await window.crypto.subtle.importKey(
    'spki',
    keyData,
    ALGORITHM,
    true,
    ['encrypt']
  );
};

// Import a private key from Base64
const importPrivateKey = async (privateKeyBase64) => {
  const keyData = base64ToArrayBuffer(privateKeyBase64);
  return await window.crypto.subtle.importKey(
    'pkcs8',
    keyData,
    ALGORITHM,
    true,
    ['decrypt']
  );
};

// Generate a random AES key for message encryption
const generateAESKey = async () => {
  return await window.crypto.subtle.generateKey(
    AES_ALGORITHM,
    true,
    ['encrypt', 'decrypt']
  );
};

// Encrypt a message using hybrid encryption (RSA + AES)
export const encryptMessage = async (message, recipientPublicKeyBase64) => {
  try {
    // Generate a random AES key for this message
    const aesKey = await generateAESKey();
    
    // Generate random IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt the message with AES
    const encoder = new TextEncoder();
    const messageData = encoder.encode(message);
    
    const encryptedMessage = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      messageData
    );
    
    // Export the AES key
    const aesKeyExported = await window.crypto.subtle.exportKey('raw', aesKey);
    
    // Encrypt the AES key with recipient's RSA public key
    const recipientPublicKey = await importPublicKey(recipientPublicKeyBase64);
    const encryptedAESKey = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      recipientPublicKey,
      aesKeyExported
    );
    
    // Return encrypted package
    return {
      encryptedMessage: arrayBufferToBase64(encryptedMessage),
      encryptedKey: arrayBufferToBase64(encryptedAESKey),
      iv: arrayBufferToBase64(iv),
      encrypted: true,
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    // Return unencrypted if encryption fails
    return {
      content: message,
      encrypted: false,
    };
  }
};

// Decrypt a message using hybrid decryption
export const decryptMessage = async (encryptedData, privateKeyBase64) => {
  try {
    if (!encryptedData.encrypted) {
      return encryptedData.content;
    }
    
    const privateKey = await importPrivateKey(privateKeyBase64);
    
    // Decrypt the AES key with RSA private key
    const encryptedAESKey = base64ToArrayBuffer(encryptedData.encryptedKey);
    const aesKeyData = await window.crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      privateKey,
      encryptedAESKey
    );
    
    // Import the AES key
    const aesKey = await window.crypto.subtle.importKey(
      'raw',
      aesKeyData,
      AES_ALGORITHM,
      true,
      ['decrypt']
    );
    
    // Decrypt the message with AES
    const iv = base64ToArrayBuffer(encryptedData.iv);
    const encryptedMessage = base64ToArrayBuffer(encryptedData.encryptedMessage);
    
    const decryptedData = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      encryptedMessage
    );
    
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('Decryption failed:', error);
    return '[Unable to decrypt message]';
  }
};

// Store keys securely in localStorage (in production, use IndexedDB with encryption)
const KEYS_STORAGE_KEY = 'chatbuzzz_e2ee_keys';

export const storeKeys = (userId, keys) => {
  const allKeys = JSON.parse(localStorage.getItem(KEYS_STORAGE_KEY) || '{}');
  allKeys[userId] = keys;
  localStorage.setItem(KEYS_STORAGE_KEY, JSON.stringify(allKeys));
};

export const getStoredKeys = (userId) => {
  const allKeys = JSON.parse(localStorage.getItem(KEYS_STORAGE_KEY) || '{}');
  return allKeys[userId] || null;
};

export const hasStoredKeys = (userId) => {
  return getStoredKeys(userId) !== null;
};

// Initialize E2EE for a user - generates keys if not exists
export const initializeE2EE = async (userId) => {
  let keys = getStoredKeys(userId);
  
  if (!keys) {
    keys = await generateKeyPair();
    storeKeys(userId, keys);
  }
  
  return keys;
};

// Cache for other users' public keys
const publicKeyCache = {};

export const cachePublicKey = (userId, publicKey) => {
  publicKeyCache[userId] = publicKey;
};

export const getCachedPublicKey = (userId) => {
  return publicKeyCache[userId] || null;
};
